import { CurrencyPropertyHourlySeriesPoint } from '../../../graphql/generated';

export const getTimeString = (timestamp: string) => {
  const date = new Date(timestamp);
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const generateYTicks = (min: number, max: number) => {
  const result = [];
  for (let i = Math.ceil(min / 10) * 10; i <= Math.ceil(max / 10) * 10; i += 10) {
    result.push(i);
  }
  return result;
};

export const getTicks = (
  data?: CurrencyPropertyHourlySeriesPoint[] | undefined,
): {
  yTicks: number[];
  domain: [number, number];
  xTicks: string[];
} => {
  if (!data || data.length === 0) {
    return { yTicks: [], domain: [0, 0], xTicks: [] };
  }

  const maxPrice = Math.ceil(Math.max(...data.map((block) => parseFloat(block.value) * 100)));
  const minPrice = Math.floor(Math.min(...data.map((block) => parseFloat(block.value) * 100)));
  const yTicks = generateYTicks(minPrice, maxPrice);

  // Show ticks every 6 hours (24 points)
  const xTickIndices: number[] =
    data.length >= 96 ? [0, 24, 48, 72] : [0, 1, 2, 3].filter((i) => i < data.length);

  const xTicks = xTickIndices.map((index) => data[index]?.timestamp).filter(Boolean);

  const domain: [number, number] = [Math.floor(minPrice / 10) * 10, Math.ceil(maxPrice / 10) * 10];
  return { yTicks, domain, xTicks };
};

export const getNextInterval = (timestamp: string) => {
  const date = new Date(timestamp);
  date.setMinutes(date.getMinutes() + 15);
  return date;
};

export const getIntervalPeriod = (timestamp: string) => {
  const nextInterval = getNextInterval(timestamp);
  return `${getTimeString(timestamp)} - ${getTimeString(nextInterval.toISOString())}`;
};

export const getCurrentDataIndex = (data: CurrencyPropertyHourlySeriesPoint[]): number => {
  if (!data || data.length === 0) return 0;

  const now = new Date();
  const currentHour = now.getHours();
  const currentMinutes = now.getMinutes();

  // Calculate quarter index
  const quarterHour = Math.floor(currentMinutes / 15);
  const quarterIndex = currentHour * 4 + quarterHour;
  return Math.min(quarterIndex, data.length - 1);
};

export const getDataWithCentPrices = (priceDataSeries: CurrencyPropertyHourlySeriesPoint[]) => {
  return priceDataSeries.map((item) => ({
    ...item,
    value: parseFloat(item.value) * 100,
  }));
};

export const getTickValues = (
  priceDataSeries: CurrencyPropertyHourlySeriesPoint[],
  defaultIndex: number,
) => {
  const centPriceData = getDataWithCentPrices(priceDataSeries);

  const pastData = centPriceData.map((item, index) =>
    index <= defaultIndex
      ? item
      : {
          ...item,
          value: null,
        },
  );

  const futureData = centPriceData.map((item, index) =>
    index >= defaultIndex
      ? item
      : {
          ...item,
          value: null,
        },
  );
  return { pastData, futureData, centPriceData };
};
